import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";

// W5 (26 Oct 2026): the options scanner rows come from the worker, which does not know the trader's entry rule. The
// backend says, per expiry, whether a NEW trade may be entered now (EXPIRED / EXPIRY_DAY / the saved minimum days /
// the pre-expiry exit hold window - the ticket's own checks), and a refused row is shown BLOCKED with that reason.
const REFRESH_MS = 60000;
export const exchangeOf = (r) => (r?.ce || {}).exchange || r?.exchange || "NFO";

/** {`${exchange}|${expiry}`: window} for the expiries in `rows`, refreshed every minute. */
export function useEntryWindows(rows) {
  const keys = useMemo(() => [...new Set((rows || []).filter((r) => r?.expiry).map((r) => `${exchangeOf(r)}|${r.expiry}`))].sort().join(","), [rows]);
  const [windows, setWindows] = useState({});
  useEffect(() => {
    if (!keys) return undefined;
    let live = true;
    const byEx = {};
    for (const k of keys.split(",")) { const [ex, e] = k.split("|"); (byEx[ex] = byEx[ex] || []).push(e); }
    const load = () => Promise.all(Object.entries(byEx).map(([ex, exps]) =>
      api.get("/options-hedge/entry-window", { params: { exchange: ex, expiry: exps } })
        .then((r) => Object.fromEntries(Object.entries(r.data?.windows || {}).map(([e, w]) => [`${ex}|${e}`, w])))
        .catch(() => ({}))))
      .then((parts) => { if (live) setWindows(Object.assign({}, ...parts)); });
    load();
    const t = setInterval(load, REFRESH_MS);
    return () => { live = false; clearInterval(t); };
  }, [keys]);
  return windows;
}

/** The row with its state BLOCKED when the backend refuses a new trade on its expiry; otherwise unchanged. */
export function withEntryWindow(row, windows) {
  const w = windows?.[`${exchangeOf(row)}|${row?.expiry}`];
  if (!w || w.ok !== false) return row;
  const reasons = [w.reason, ...(w.reason === "DTE_BELOW_MIN" && w.exit_schedule_allows ? ["EXIT_SCHEDULE_WOULD_ALLOW"] : [])];
  return { ...row, state: { state: "BLOCKED", reasons }, entry_window: w };
}
